import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle, WifiOff, WifiSync, Loader2, Mail } from "lucide-react";
import { useState, useEffect } from "react";

interface ServerDownOverlayProps {
  onRetry: () => void;
  isRetrying: boolean;
  lastChecked: Date | null;
}

/**
 * Overlay qui s'affiche par-dessus l'application quand le serveur est inaccessible.
 * Conserve l'URL et permet de réessayer.
 */
const formatLastChecked = (date: Date | null): string => {
  if (!date) return "";

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(Math.abs(diffMs) / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);

  if (diffSeconds < 60) {
    return `il y a ${diffSeconds} seconde${diffSeconds > 1 ? "s" : ""}`;
  } else if (diffMinutes < 60) {
    return `il y a ${diffMinutes} minute${diffMinutes > 1 ? "s" : ""}`;
  } else {
    return `à ${date.toLocaleTimeString("fr-FR", {
      hour: "2-digit",
      minute: "2-digit",
    })}`;
  }
};

export function ServerDownOverlay({ onRetry, isRetrying, lastChecked }: ServerDownOverlayProps) {
  const [lastCheckedText, setLastCheckedText] = useState(() => formatLastChecked(lastChecked));

  // Met à jour le texte toutes les secondes
  useEffect(() => {
    // Mise à jour immédiate quand lastChecked change
    queueMicrotask(() => setLastCheckedText(formatLastChecked(lastChecked)));

    const interval = setInterval(() => {
      setLastCheckedText(formatLastChecked(lastChecked));
    }, 1000);

    return () => clearInterval(interval);
  }, [lastChecked]);

  const generateContactEmail = () => {
    const subject = encodeURIComponent("Problème de connexion - Nautika");
    const body = encodeURIComponent(`Bonjour,

J'ai rencontré un problème de connexion avec l'application Nautika.

Détails techniques :
- URL actuelle : ${window.location.href}
- Heure du problème : ${new Date().toLocaleString("fr-FR")}
- Navigateur : ${navigator.userAgent}
- Dernière vérification : ${lastCheckedText || "N/A"}

Merci de votre aide.

Cordialement,`);

    return `mailto:support@nautika.fr?subject=${subject}&body=${body}`;
  };

  return (
    <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm">
      <div className="flex min-h-screen flex-col items-center justify-center px-4 text-center">
        {/* Icône principale - WiFi ou loader */}
        <div className="relative mb-6 rounded-full bg-red-100 p-6">
          {isRetrying ? <Loader2 className="h-16 w-16 animate-spin text-red-600" /> : <WifiOff className="h-16 w-16 text-red-600" />}
        </div>

        {/* Titre principal */}
        <div className="mb-4 text-4xl font-bold text-foreground">Problème de connexion</div>

        {/* Sous-titre */}
        <div className="mb-6 text-xl font-semibold text-muted-foreground">Nous n'arrivons pas à joindre nos serveurs</div>

        {/* Message explicatif */}
        <p className="mb-8 max-w-md text-muted-foreground">
          Il semble que nos serveurs soient temporairement indisponibles. Cela peut être dû à une maintenance ou un problème technique.
        </p>

        {/* Alerte avec détails */}
        <Alert className="mb-8 max-w-md">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Information technique</AlertTitle>
          <AlertDescription className="mt-2">
            <div className="space-y-1 text-sm">
              <div>Nous tentons automatiquement de nous reconnecter</div>
              <div>Vous pouvez forcer une nouvelle tentative</div>
              {lastChecked && <div className="mt-2 text-xs text-muted-foreground">Dernière vérification {lastCheckedText}</div>}
            </div>
          </AlertDescription>
        </Alert>

        {/* Boutons d'action */}
        <div className="flex gap-4">
          <Button onClick={onRetry} disabled={isRetrying} size="lg" className="min-w-32">
            {isRetrying ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Vérification...
              </>
            ) : (
              <>
                <WifiSync className="mr-2 h-4 w-4" />
                Réessayer
              </>
            )}
          </Button>

          <Button variant="outline" size="lg" asChild>
            <a href={generateContactEmail()}>
              <Mail className="mr-2 h-4 w-4" />
              Nous contacter
            </a>
          </Button>
        </div>

        {/* Message de rassurance */}
        <p className="mt-8 text-sm text-muted-foreground">Vos données sont sauvegardées et aucune action n'est perdue.</p>
      </div>
    </div>
  );
}
